import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, Button, IconButton, Typography } from '@material-ui/core';
import AndroidIcon from '@material-ui/icons/Android';
import AppleIcon from '@material-ui/icons/Apple';
import CloseIcon from '@material-ui/icons/Close';
import { PLAY_STORE_URL, APP_STORE_URL } from '../../constants/app.constant';
import {
  DOWNLOAD_APP_ROUTE,
  TEST_ATTEMPT_ROUTE,
  TEST_ATTEMPT_PREVIEW_ROUTE,
  TEST_PREPARATION_MODE_ROUTE,
} from '../../constants/route.constant';
import { useDeviceDetector } from '../../hooks/useDeviceDetector';
import { authService } from '../../services';
import logo from '../../../assets/images/logo.png';

import './appPromoDialog.scss';

// We run two independent 3-show cycles, keyed separately in localStorage:
//   - pre-auth  : a logged-out visitor browsing the site
//   - post-auth : after OTP verification (auth triggers a full page reload, so a
//                 fresh mount with a token in storage means we're in this cycle)
const PRE_AUTH_COUNT_KEY = 'app_promo_pre_auth_count';
const POST_AUTH_COUNT_KEY = 'app_promo_post_auth_count';

// Timestamp (ms) marking when the current cycle's window opened. The cycle
// resets once RESET_MS has elapsed since this anchor. Stored alongside the
// count so the reset is a pure timestamp comparison, never a background timer.
const PRE_AUTH_WINDOW_KEY = 'app_promo_pre_auth_window_start';
const POST_AUTH_WINDOW_KEY = 'app_promo_post_auth_window_start';

const MAX_SHOWS = 3;

// The 3-show cycle resets (counter back to 0) once this much time has passed
// since the cycle started, so an active user can be re-pitched periodically.
const RESET_MS = 15 * 60_000;

// Gap (ms) BEFORE the show at each index, measured from the previous show.
// First show fires on scroll (gap 0), 2nd one minute later, 3rd three minutes after that.
const GAPS = [0, 60_000, 180_000];

// How far the user must scroll before we treat it as "actively browsing".
const SCROLL_THRESHOLD = 120;

// Lower bound between scroll evaluations so a fast scroll burst doesn't hammer
// localStorage. Cheap guard; the handler itself only does a few reads.
const SCROLL_THROTTLE_MS = 1_000;

// Re-check interval when a show is deferred because something is blocking it
// (an auth/subscription modal is open, or we're on an excluded route).
const RETRY_MS = 4_000;

// Route prefixes where the promo must never appear. We strip the param portion
// ("/:attemptId") so a simple startsWith check matches the live pathname.
const prefixOf = (path: string) => path.split('/:')[0];
const EXCLUDED_PREFIXES = [
  prefixOf(TEST_ATTEMPT_ROUTE.path),
  prefixOf(TEST_ATTEMPT_PREVIEW_ROUTE.path),
  prefixOf(TEST_PREPARATION_MODE_ROUTE.path),
  prefixOf(DOWNLOAD_APP_ROUTE.path),
];

const readCount = (key: string) => {
  try {
    return parseInt(localStorage.getItem(key) || '0', 10) || 0;
  } catch {
    return 0;
  }
};

const writeCount = (key: string, n: number) => {
  try {
    localStorage.setItem(key, String(n));
  } catch {
    // ignore storage failures (private mode etc.)
  }
};

const readTimestamp = (key: string) => {
  try {
    return parseInt(localStorage.getItem(key) || '0', 10) || 0;
  } catch {
    return 0;
  }
};

const writeTimestamp = (key: string, ts: number) => {
  try {
    localStorage.setItem(key, String(ts));
  } catch {
    // ignore storage failures (private mode etc.)
  }
};

const removeKey = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore storage failures
  }
};

const onExcludedRoute = () => EXCLUDED_PREFIXES.some((p) => window.location.pathname.startsWith(p));

// True when another modal (login, signup, subscription, …) is open. Our own
// dialog carries the `app-promo-dialog` class, so we exclude it from the match.
// This guarantees the promo never overlays or fights an auth modal.
const otherDialogOpen = () => document.querySelectorAll('.MuiDialog-root:not(.app-promo-dialog)').length > 0;

export const AppPromoDialog = () => {
  const { isAndroid, isIOS, isTablet, isInAppWebView, isIOSWebView } = useDeviceDetector();
  const [open, setOpen] = useState(false);

  const timersRef = useRef<number[]>([]);
  const cycleActiveRef = useRef(false);
  const lastScrollEvalRef = useRef(0);

  useEffect(() => {
    // Android/iOS phones and tablets only, and never inside a native app's WebView.
    if (!(isAndroid || isIOS) || !isTablet || isInAppWebView || isIOSWebView) return;

    // Reading the token can touch JSON.parse internally, so guard it: a corrupt
    // token must never crash the promo (or the app). Default to the pre-auth cycle.
    let loggedIn = false;
    try {
      loggedIn = authService.isLoggedIn;
    } catch {
      loggedIn = false;
    }

    const countKey = loggedIn ? POST_AUTH_COUNT_KEY : PRE_AUTH_COUNT_KEY;
    const windowKey = loggedIn ? POST_AUTH_WINDOW_KEY : PRE_AUTH_WINDOW_KEY;

    let cancelled = false;

    const clearTimers = () => {
      timersRef.current.forEach((id) => clearTimeout(id));
      timersRef.current = [];
    };

    const addTimer = (fn: () => void, ms: number) => {
      const id = window.setTimeout(fn, ms);
      timersRef.current.push(id);
    };

    // Pure timestamp comparison (no timer): if the cycle's window opened more
    // than RESET_MS ago, wipe the count so the next scroll starts a fresh cycle.
    const maybeReset = () => {
      const start = readTimestamp(windowKey);
      if (start && Date.now() - start >= RESET_MS) {
        writeCount(countKey, 0);
        removeKey(windowKey);
        cycleActiveRef.current = false;
        clearTimers();
      }
    };

    // Show one occurrence (the one at the current stored count). If something is
    // blocking it, defer and retry without consuming the slot.
    const showOne = () => {
      if (cancelled) return;
      const count = readCount(countKey);
      if (count >= MAX_SHOWS) {
        cycleActiveRef.current = false;
        return;
      }

      if (onExcludedRoute() || otherDialogOpen()) {
        addTimer(showOne, RETRY_MS);
        return;
      }

      setOpen(true);
      const newCount = count + 1;
      writeCount(countKey, newCount);
      // Anchor the reset window at the first show of the cycle.
      if (newCount === 1) writeTimestamp(windowKey, Date.now());

      if (newCount < MAX_SHOWS) {
        addTimer(showOne, GAPS[newCount]);
      } else {
        cycleActiveRef.current = false; // cycle complete
      }
    };

    // The first show of a cycle is gated on the user actively scrolling. The
    // listener stays attached for the session so a scroll after a 15-min reset
    // can kick off the next cycle. Throttled to keep scroll cheap.
    const onScroll = () => {
      if (cancelled) return;
      if (window.scrollY < SCROLL_THRESHOLD) return;

      const now = Date.now();
      if (now - lastScrollEvalRef.current < SCROLL_THROTTLE_MS) return;
      lastScrollEvalRef.current = now;

      maybeReset();
      if (cycleActiveRef.current) return; // a cycle is already running
      if (readCount(countKey) >= MAX_SHOWS) return; // exhausted, awaiting a reset

      cycleActiveRef.current = true;
      showOne();
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      cancelled = true;
      window.removeEventListener('scroll', onScroll);
      clearTimers();
      // Release the in-flight flag so a re-run (e.g. a viewport flag flipping)
      // can resume cleanly rather than staying stuck "active" with no timers.
      cycleActiveRef.current = false;
    };
  }, [isAndroid, isIOS, isTablet, isInAppWebView, isIOSWebView]);

  const dismiss = () => setOpen(false);

  return (
    <Dialog open={open} onClose={dismiss} className="app-promo-dialog" aria-labelledby="app-promo-title">
      <DialogContent className="app-promo-content">
        <IconButton className="app-promo-close" size="small" aria-label="close" onClick={dismiss}>
          <CloseIcon fontSize="inherit" />
        </IconButton>

        <div className="app-promo-logo-wrap">
          <img src={logo} alt="Xarothi" className="app-promo-logo" />
        </div>

        <Typography component="h2" id="app-promo-title" className="app-promo-title">
          Get the Xarothi app
        </Typography>
        <Typography component="p" className="app-promo-subtitle">
          Get the full Xarothi experience on your phone. Practice mock tests, track your
          progress, get exam alerts, and stay sharp with daily current affairs.
        </Typography>

        <Button
          href={isIOS ? APP_STORE_URL : PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          size="large"
          className="app-promo-cta"
          startIcon={isIOS ? <AppleIcon /> : <AndroidIcon />}
          onClick={dismiss}
        >
          {isIOS ? 'Download on the App Store' : 'Get it on Google Play'}
        </Button>

        <Button className="app-promo-dismiss" size="small" onClick={dismiss}>
          Continue in browser
        </Button>
      </DialogContent>
    </Dialog>
  );
};
